import { memo } from 'react'
import { X, Link } from 'lucide-react'
import { cn } from '@wellesley/ui'
import { Card } from '@wellesley/openapi'
import { useSwitchState } from 'hooks/useSwitchState'
import useImageMeta from 'hooks/useImageMeta'
import LinkPreviewWrapper from './components/LinkPreviewWrapper'
import { TLinkPreview } from '../types'

export type TGeneralLinkPreviewProps = Pick<
  TLinkPreview,
  'showImage' | 'onClose'
> & {
  card: Card
}

const GeneralLinkPreview = memo<TGeneralLinkPreviewProps>(
  ({ card, showImage = true, onClose }) => {
    const { url, title, description, image, icon } = card

    const meta = useImageMeta(showImage ? image : undefined)

    const [isFaviconError, setIsFaviconError] = useSwitchState()

    const linkPathname = url?.split('/')[2]
    const shouldRenderImage = showImage && !!image

    return (
      <LinkPreviewWrapper
        url={url}
        className="relative flex cursor-pointer flex-col text-gray-700"
      >
        {onClose && (
          <X
            className="right-xs top-xs absolute cursor-pointer"
            data-testid="link-preview-close"
            aria-label="Close link preview"
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onClose()
            }}
            size={24}
            color="var(--gray-500)"
          />
        )}

        <div className="rounded-m overflow-hidden border border-gray-300">
          {shouldRenderImage && (
            <div className="flex w-full max-w-full">
              <img
                data-testid="link-preview-image"
                referrerPolicy="no-referrer"
                className="size-xs h-full w-full object-cover duration-200"
                alt={description || ''}
                src={image}
                style={
                  meta?.loaded
                    ? { opacity: 1, aspectRatio: String(meta.ratio) }
                    : { opacity: 0, height: 0 }
                }
              />
            </div>
          )}

          <div className="p-xs gap-xxxs flex flex-col justify-center">
            <div className="gap-xxxs flex">
              {icon && !isFaviconError ? (
                <img
                  onError={setIsFaviconError}
                  referrerPolicy="no-referrer"
                  className="size-xxs rounded-xs border border-gray-200 object-cover"
                  src={icon}
                  alt={title || ''}
                />
              ) : (
                <Link size={20} color="var(--gray-700)" />
              )}

              <h3 className="flex items-center text-sm font-medium text-blue-500">
                {linkPathname}
              </h3>
            </div>
            {title && (
              <p
                data-testid="link-preview-title"
                className="line-clamp-2 text-base font-semibold"
              >
                {title}
              </p>
            )}
            {description && (
              <p
                data-testid="link-preview-description"
                className={cn(
                  'text-sm',
                  title ? 'line-clamp-2' : 'line-clamp-3',
                )}
              >
                {description}
              </p>
            )}
          </div>
        </div>
      </LinkPreviewWrapper>
    )
  },
)

GeneralLinkPreview.displayName = 'GeneralLinkPreview'

export default GeneralLinkPreview
